import { useQuery } from '@tanstack/react-query'
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'

import { trpc } from '@maxus/website/lib'
import { cn } from '@maxus/website/utils'

import { useConfirmModal } from '../../base'
import { useDrawer } from '../../hooks'
import { ProjectPanel } from './project-panel'
import type {
  ProjectCommonFormValues,
  ProjectCreateFormValues,
  ProjectDrawerHandle,
  ProjectDrawerParams,
  ProjectFormValues,
} from './type'

export type { ProjectCommonFormValues, ProjectCreateFormValues, ProjectDrawerHandle, ProjectDrawerParams } from './type'

export const ProjectDrawer = forwardRef<ProjectDrawerHandle>((_, ref) => {
  const { t } = useTranslation()
  const { confirm: confirmClose } = useConfirmModal()
  const {
    active,
    entered,
    closing,
    close,
    complete,
    open,
    params,
  } = useDrawer<ProjectDrawerParams>()
  const isCreating = params?.mode === 'create'
  const [projectSearch, setProjectSearch] = useState('')
  const [debouncedProjectSearch, setDebouncedProjectSearch] = useState('')
  const externalProjectsQuery = useQuery({
    ...trpc.project.search.queryOptions(debouncedProjectSearch),
    enabled: active && isCreating,
  })
  const templatesQuery = useQuery({
    ...trpc.project.profileTemplate.list.queryOptions(),
    enabled: active && isCreating,
  })
  const nasStoragesQuery = useQuery({
    ...trpc.project.listNasStorages.queryOptions(),
    enabled: active && isCreating,
  })

  useEffect(
    () => {
      if (!active) {
        setProjectSearch('')
        setDebouncedProjectSearch('')
        return
      }

      const timeout = window.setTimeout(
        () => {
          setDebouncedProjectSearch(projectSearch)
        },
        250,
      )
      return () => window.clearTimeout(timeout)
    },
    [active, projectSearch],
  )

  useImperativeHandle(
    ref,
    () => ({
      open: nextParams => open(nextParams),
    }),
  )

  if (!active || !params) {
    return null
  }

  const requestClose = () => {
    confirmClose({
      title: t('Close project?'),
      description: t('Your edits are unsaved and will be discarded on close.'),
      onConfirm: close,
    })
  }

  const confirm = (values: ProjectFormValues) => {
    complete(() => {
      if (params.mode === 'create') {
        params.onConfirm(values satisfies ProjectCreateFormValues)
        return
      }

      const commonValues = {
        davinciProjectName: values.davinciProjectName,
        description: values.description,
        localNasStorageKey: values.localNasStorageKey,
        name: values.name,
      } satisfies ProjectCommonFormValues
      params.onConfirm(commonValues)
    })
  }

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex justify-end bg-black/40 transition-colors',
        (!entered || closing) && 'bg-black/0',
      )}
      onPointerDown={event => {
        if (event.target === event.currentTarget) {
          requestClose()
        }
      }}
    >
      <div
        className={cn(
          'h-full w-125 transition-transform duration-[240ms] ease-out',
          (!entered || closing) && 'translate-x-full',
        )}
        onClick={event => event.stopPropagation()}
        role="dialog"
      >
        <ProjectPanel
          externalProjects={externalProjectsQuery.data ?? []}
          isExternalProjectsLoading={externalProjectsQuery.isLoading}
          isNasStoragesLoading={nasStoragesQuery.isLoading}
          isTemplatesLoading={templatesQuery.isLoading}
          nasStorages={nasStoragesQuery.data ?? []}
          onClose={requestClose}
          onConfirm={confirm}
          onProjectSearchChange={setProjectSearch}
          params={params}
          templates={templatesQuery.data?.items ?? []}
        />
      </div>
    </div>
  )
})

ProjectDrawer.displayName = 'ProjectDrawer'
